import { useGlobalStore } from '@/stores';
import i18n from '../i18n';
import { showValidationMessage } from './Notification';

const RouteType = {
    OnwardRoute: 1,
    SIPTrunk: 2,
    CustomService: 3,
    Centrex: 4,
    Voicemail: 5,
    PlayPrompt: 6,
    PercentageSplit: 7,
    WebConferenceManager: 9,
};

const t = (message) => {
    return i18n.global.t(message);
};

const getAccessPointWarning = (warning, messages) => {
    return warning
        .replace(/\[a]/g, messages[0] ? messages[0].total : 0)
        .replace(
            /\[b]/g,
            (messages[0] ? messages[0].points : [])
                .map((point) => t(`[x] (${point.type})`).replace(/\[x]/g, point.name))
                .join('\n') + (messages[0] && messages[0].total > 10 ? '\n...' : ''),
        )
        .replace(/\[c]/g, messages.length > 1 ? messages[1].total : 0)
        .replace(
            /\[d]/g,
            (messages.length > 1 ? messages[1].points : [])
                .map((point) => t(`[x] (${point.type})`).replace(/\[x]/g, point.name))
                .join('\n') + (messages.length > 1 && messages[1].total > 10 ? '\n...' : ''),
        );
};

const getRoute = (type) => {
    switch (type) {
        case RouteType.OnwardRoute:
            return {
                type,
                numbers: [''],
                ringingTimeouts: [30],
                record: false,
                queue: false,
                maxQueueTime: -1,
                mailboxId: null,
            };
        case RouteType.SIPTrunk:
            return {
                type,
                number: null,
                ringingTimeout: 30,
                record: false,
                queue: false,
                maxQueueTime: -1,
                mailboxId: null,
            };
        case RouteType.Centrex:
            return { type, mappingType: 2 };
        case RouteType.Voicemail:
            return { type, mappingType: 2 };
        default:
            return { type };
    }
};

export const getRouteTypeLabels = (unassigned = true) => {
    return [
        unassigned ? '[' + t('UNASSIGNED') + ']' : '',
        t('ONWARD_ROUTE'),
        t('SIP_TRUNK'),
        t('IVR_SERVICE'),
        t('Centrex'),
        t('VOICEMAIL'),
        t('Play Prompt'),
        t('PERCENTAGE_SPLIT'),
        '',
        t('Web Conference Manager'),
    ];
};

const getRouteTypes = (routeType = 0, accessType = 0, all = false) => {
    const store = useGlobalStore();
    const types = [];

    if (all || [0, 1].includes(accessType) || [RouteType.OnwardRoute, RouteType.SIPTrunk].includes(routeType)) {
        types.push({ id: RouteType.OnwardRoute, name: 'ONWARD_ROUTE' });
        if (all || store.hasRight('USE_SIP_TRUNKS') || routeType === RouteType.SIPTrunk) {
            types.push({ id: RouteType.SIPTrunk, name: 'SIP_TRUNK' });
        }
    }

    if (all || store.routeData.scripts.length > 0 || routeType === RouteType.CustomService) {
        types.push({ id: RouteType.CustomService, name: 'IVR_SERVICE' });
    }

    if (
        all ||
        store.routeData.treatments.length > 0 ||
        store.routeData.users.length > 0 ||
        routeType === RouteType.Centrex
    ) {
        types.push({ id: RouteType.Centrex, name: 'Centrex' });
    }

    if (
        all ||
        [0, 1].includes(accessType) ||
        [RouteType.Voicemail, RouteType.PlayPrompt, RouteType.PercentageSplit, RouteType.WebConferenceManager].includes(
            routeType,
        )
    ) {
        if (all || store.routeData.voicemailProfiles.length > 0 || routeType === RouteType.Voicemail) {
            types.push({ id: RouteType.Voicemail, name: 'VOICEMAIL' });
        }
        if (all || store.routeData.mediaLists.length > 0 || routeType === RouteType.PlayPrompt) {
            types.push({ id: RouteType.PlayPrompt, name: 'Play Prompt' });
        }
        if (all || store.hasRight('VIEW_PERCENT_SPLIT') || routeType === RouteType.PercentageSplit) {
            types.push({ id: RouteType.PercentageSplit, name: 'PERCENTAGE_SPLIT' });
        }
        if (
            all ||
            store.hasRight('DTA_INTEGRATION_WebConferenceManager') ||
            routeType === RouteType.WebConferenceManager
        ) {
            types.push({ id: RouteType.WebConferenceManager, name: 'Web Conference Manager' });
        }
    }

    return types;
};

const getUpdatedRoute = (current, field, val) => {
    if (field === 'type') {
        return getRoute(val);
    }

    const route = { ...current };
    route[field] = val;
    if (route.type === RouteType.PlayPrompt && field === 'mappingType') {
        route.mappingId = null;
    } else if ([RouteType.OnwardRoute, RouteType.SIPTrunk].includes(route.type) && field === 'mediaListId') {
        route.whisperPromptId = route.whisperPromptId !== null && route.whisperPromptId !== undefined ? 0 : null;
        route.failurePromptId = route.failurePromptId !== null && route.failurePromptId !== undefined ? 0 : null;
        route.holdPromptId = route.holdPromptId !== null && route.holdPromptId !== undefined ? 0 : null;
    }

    return route;
};

const loadInitialPrompts = (route, loader, callback, orgId) => {
    const store = useGlobalStore();
    if (
        route.type === RouteType.CustomService &&
        store.routeData.splitProfiles.some((y) => y.scriptId === route.scriptId)
    ) {
        route.type = RouteType.PercentageSplit;
    }

    if ([RouteType.OnwardRoute, RouteType.SIPTrunk].includes(route.type)) {
        loadPrompts(route.mediaListId, loader, callback, orgId);
    }

    if (route.type === RouteType.PlayPrompt) {
        loadPrompts(route.mappingType, loader, callback, orgId);
    }
};

const loadPrompts = (mediaListId, loader, callback, orgId) => {
    if (mediaListId > 0) {
        const store = useGlobalStore();
        const index = store.routeData.mediaLists.findIndex((x) => x.mediaListId === mediaListId);
        if (!store.routeData.loadedLists.includes(mediaListId)) {
            store.routeData.loadedLists.push(mediaListId);
            loader('prompts/get', { orgId: orgId ? orgId : store.user.orgId, mediaListId }, (response) => {
                store.routeData.mediaLists[index].prompts = response.prompts;
                if (callback) {
                    callback();
                }
            });
        }
    }
};

const validateRoute = (route) => {
    if ([RouteType.OnwardRoute, RouteType.SIPTrunk].includes(route.type)) {
        if (route.mailboxId === 0) {
            showValidationMessage('Invalid Voicemail Profile');
            return false;
        }
        if (route.mediaListId && route.whisperPromptId === 0) {
            showValidationMessage('Invalid Whisper Prompt');
            return false;
        }
    }
    if (route.type === RouteType.OnwardRoute) {
        if (route.mediaListId && route.holdPromptId === 0) {
            showValidationMessage('Invalid Hold Prompt');
            return false;
        }
        if (route.mediaListId && route.failurePromptId === 0) {
            showValidationMessage('Invalid Call Failure Prompt');
            return false;
        }
        if (route.numbers.some((number) => number.trim() === '')) {
            showValidationMessage('Outbound Number is a required field');
            return false;
        }
    }
    if (route.type === RouteType.SIPTrunk && !route.trunkId) {
        showValidationMessage('Invalid SIP Trunk');
        return false;
    }
    if (route.type === RouteType.CustomService && !route.scriptId) {
        showValidationMessage('Invalid Custom Service');
        return false;
    }
    if (route.type === RouteType.Centrex) {
        if (route.mappingType === 1 && !route.mappingId) {
            showValidationMessage('Invalid User');
            return false;
        }
        if (route.mappingType === 2 && !route.mappingId) {
            showValidationMessage('Invalid Call Treatment');
            return false;
        }
    }
    if (route.type === RouteType.Voicemail && !route.mailboxId) {
        showValidationMessage('Invalid Voicemail Profile');
        return false;
    }
    if (route.type === RouteType.PlayPrompt && (!route.mappingType || !route.mappingId)) {
        showValidationMessage('Invalid Prompt');
        return false;
    }
    if (route.type === RouteType.PercentageSplit && !route.scriptId) {
        showValidationMessage('Invalid Percentage Split');
        return false;
    }

    return true;
};

export {
    getAccessPointWarning,
    getRoute,
    getRouteTypes,
    getUpdatedRoute,
    loadInitialPrompts,
    loadPrompts,
    RouteType,
    validateRoute,
};
